import type { BanDich } from '../../types';
import type { viKhuon } from './khuon.vi';

export const enKhuon: Pick<BanDich, keyof typeof viKhuon> = {
  troLyAiKhuon: {
    nhom: 'Business groups',
    themNhom: 'Add group',
    xoaNhom: 'Delete group',
    doiTenNhom: 'Rename group',
    themKhuon: 'Add a query template to this group',
    thaoTacNhom: 'Actions for group {{ten}}',
    xoaNhomXacNhan: 'Delete group “{{ten}}”?',
    xoaNhomXacNhanMoTa: 'The group is removed from the business list. This cannot be undone.',
    chuaCoNhom: 'No groups yet',
    nhomRong: 'no query templates yet',
    tenNhomPlaceholder: 'For example: Water, Electricity',
    tenNhom: 'Group name',
    chonMotKhuon: 'Pick a query template on the left, or add a new one to a group',
    batDauBangNhom: 'Start with a business group — for example “Water”',
    ten: 'Template name',
    tenPlaceholder: 'For example: Revenue by month and region',
    thuocNhom: 'Group',
    cachGoi: 'Other names',
    cachGoiPlaceholder: 'revenue, sales, cash in',
    cachGoiViSao: 'Other ways people refer to the same business query. The assistant uses them to recognize which template fits.',
    cauHoiMau: 'Typical questions',
    cauHoiMauPlaceholder: 'What was revenue in July?\nRevenue by region this quarter?',
    cauHoiMauViSao:
      'One question per line. They help the assistant recognize the template and serve as a test set when you change the query.',
    cauLenh: 'SQL query ({{pn}})',
    cauLenhPlaceholder:
      "SELECT thang, khu_vuc, SUM(thanh_tien) AS doanh_thu\nFROM hoa_don\nWHERE trang_thai <> '9'\nGROUP BY thang, khu_vuc",
    luu: 'Save',
    xoa: 'Delete template',
    chayThu: 'Test run',
    luuTruocKhiChay: 'Save the new query before test-running it',
    daLuu: 'Template saved.',
    daLuuChuaChayThu: 'Saved. Click Test run so this template can be used in answers.',
    daLuuNhom: 'Group saved.',
    daXoaNhom: 'Group deleted.',
    daXoa: 'Template deleted.',
    daDoiCheDo: 'Declaration mode changed.',
    sqlDoiThiPhaiChayLai:
      "The query has changed. After saving, run it again — the new query hasn't been verified, so it won't be used in answers yet.",
    chayThuXong: 'Ran successfully, returned {{n}} rows.',
    chayThuKhongDong: 'The query ran but returned no rows.',
    ketQuaTomTat: '{{n}} rows · {{ms}} ms',
    khongDongViSao:
      "The query is valid but the store has no matching data yet. The template can still be used — only review the filters if you expected numbers.",
    daSanSang: 'In use',
    chuaSanSang: 'Not in use yet',
    viSaoChuaSanSang: "The template has never been test-run, so the assistant doesn't use it yet. Click Test run.",
    phamViTieuDe: 'What the assistant can read',
    phamViViSao: "Exactly these tables and columns, nothing more. Questions that need data outside this scope can't be answered.",
    phamViChuaLuu: 'Save the template to see the scope this query opens.',
    phamViRong: "The query doesn't use any declared table yet.",
    moiCot: 'all columns (the query uses SELECT *)',
    congDonLa: 'Summable: {{cot}}',
    dangBatKhuon: 'Declaring with query templates',
    dangBatCauHoi: 'Declaring with questions',
    tomTatSanSang: '{{san}}/{{tong}} templates test-run',
    sangKhuon: 'Switch to declaring with query templates',
    veCauHoi: 'Switch back to declaring with questions',
    doiChieuTieuDe: 'Change the declaration mode?',
    gatSangKhuonNghia:
      'After switching, the assistant only answers within the declared query templates, and read access follows the “By query template” tab of Data permissions. The table/column permission matrix stops applying for now.',
    gatVeCauHoiNghia:
      'After switching, the assistant answers over all declared tables, and read access follows the table/column matrix. Query templates are kept and still used to prefill the Business declaration screen.',
    soKhuon: 'Total templates',
    soKhuonSanSang: 'Test-run',
    vaiSeHetHoiDuoc: 'These roles will not be able to ask anything',
    vaiSeHetHoiDuocViSao:
      'These roles have no query templates granted. Grant them in Data permissions, “By query template” tab, before switching.',
    khuonChuaChayThu: 'Templates not test-run — they will not be used',
    khuonBiHa: 'Templates that no longer match the current schema',
    banKhaiCu: "The declaration doesn't have columns the query uses — tables: {{bang}}",
    banKhaiCuViSao:
      "The platform checks the query against the declaration, not the actual schema. The query may still run fine on the database; this usually means the source added columns after the last declaration. Use the button below to re-declare exactly those tables — existing business declarations (column labels, code meanings, row meaning) are kept.",
    capNhatBanKhai: 'Update the declaration from the source, then save again',
    daCapNhatBanKhai: 'Declaration updated. Saving the template again...',
    khongThayBangONguon: "Table {{bang}} wasn't found in the source. It may have been renamed or deleted.",
    loaiKhuon: 'Template type',
    loaiCauLenh: 'Sample SQL query',
    loaiThuTuc: 'Procedure / function in the database',
    loaiApi: 'Business API',
    duongDanApi: 'Which API to call',
    duongDanApiViSao:
      'The path appended to the source base address. Where a part depends on the question, write {parameter_name} and declare that parameter with position «path». The base address and key come from the source — change them in Source connections.',
    duongDanApiNgan:
      'The API must return rows (list of rows) and tongHop (total row, optional). Filtering, summing and data permissions are the API’s job.',
    phuongThuc: 'Method',
    diaChiGoc: 'Base address',
    cotViTri: 'Goes into',
    viTriQuery: 'Query (?a=)',
    viTriPath: 'Path',
    viTriBody: 'Body (POST)',
    loaiCauLenhViSao:
      'The assistant still writes the SQL itself — the template only limits where it may write. More flexible: slightly different questions can still be answered.',
    loaiThuTucViSao:
      "The assistant writes no SQL — it only picks the business query and fills in parameters. Fully precise, but it can only answer what the procedure already computes. Works with PostgreSQL functions returning TABLE and SQL Server stored procedures. Requires the source in live-read mode (the replica only holds synced data, no procedures). The type can't be changed after saving.",
    tenThuTuc: 'Procedure / function name (with schema if any)',
    thamSo: 'Parameters',
    thamSoViSao:
      "List the parameters in the procedure's exact order. Fill modes: «Assistant fills» — inferred from the question, so the description is the only thing telling it months are 1-12; without one, the parameter is easily filled with a plausible value from another period. «Fixed value» — a template constant, always sent, the assistant can't change it; use it for self-scoping parameters like p_vai that the function checks with kiem_vai. «Asker's role» — the platform fills in the role of the person asking. Unfilled parameters: those with a default are omitted so the default applies; SQL Server passes NULL for parameters without a default.",
    cotHienThi: 'Display columns',
    cotHienThiViSao:
      'Set a label and format (currency, number, date, percent) for each result column. Undeclared columns show their raw name.',
    tongHop: 'Totals — JSON object {column_name: type} (2nd result set)',
    tongHopViSao:
      'Only used when the procedure returns two result sets (usually SQL Server: details, then one total row). A Postgres function returning TABLE has only one set — leave it as {}.',
    tomTat: 'Summary template',
    tomTatPlaceholder: 'Revenue {ky}: total {tong_doanh_thu}, {so_khach_hang} customers.',
    tomTatViSao:
      "Available slots: {total_column_name} · {ky} (built from the nam/thang parameters) · {so_dong} · {param:name} · {top:name_column:value_column:N}. Slots that can't be resolved are left blank, never printed as {…}.",
    tieuDe: 'Display title (optional)',
    tieuDePlaceholder: 'Revenue by customer {ky}',
    thamSoChayThu: 'Parameters for the test run',
    thamSoChayThuViSao:
      'Leave empty to run over all data — procedures written for this default to NULL and skip the condition.',
    soTap: '{{n}} result sets',
    dongTongHop: 'Total row',
    nenTangTuDien: 'Filled by the platform',
    vaiNguoiHoi: "the asker's role",
    khuonMoi: 'New query template',
    chuaLuu: 'Not saved',
    coThayDoi: 'Unsaved changes',
    phimTatLuu: 'Ctrl+S to save',
    xoaXacNhan: 'Delete this query template?',
    xoaXacNhanMoTa: 'The assistant will no longer use this template to answer. This cannot be undone.',
    tabNhanDien: '1. Recognition',
    tabLayDuLieu: '2. Data',
    tabTrinhBay: '3. Presentation',
    tabChayThu: '4. Test run & scope',
    tabCoLoi: 'Required information is missing',
    soCauHoiMau: '{{n}} questions',
    loaiCauLenhNgan: 'The assistant writes SQL within the sample query — flexible with slightly different questions.',
    loaiThuTucNgan:
      'The assistant only picks the procedure and fills in parameters — fully precise, but it only answers what the procedure computes.',
    loaiKhongDoiDuoc: "The type can't be changed after saving.",
    suaJson: 'Edit as JSON',
    xemBang: 'View as table',
    jsonLoiQuayBang: 'The JSON has an error — fix it before switching back to the table.',
    thamSoJsonGoiY:
      'Array [{ten, kieu, moTa, batBuoc, nguon, giaTri}] — kieu: int | string | date | decimal | bool; nguon: "" or "dang_nhap".',
    cotJsonGoiY: 'Array [{field, nhan, kieu}] — kieu: text | tien | ngay | so | phan_tram.',
    themThamSo: 'Add parameter',
    chuaCoThamSo: 'No parameters — the procedure will be called without any.',
    cotTen: 'Name',
    cotKieu: 'Type',
    cotCachDien: 'Fill mode',
    cotMoTaGiaTri: 'Description / value',
    cotBatBuoc: 'Required',
    dienTroLi: 'Assistant fills',
    dienCoDinh: 'Fixed value',
    dienDangNhap: "Asker's role",
    moTaThamSoPlaceholder: 'e.g. month 1-12',
    thieuMoTa: 'No description — the assistant may fill in a wrong value.',
    boDong: 'Remove this row',
    themCot: 'Add column',
    layCotTuKetQua: 'Take {{n}} columns from the test run',
    chuaCoCot: 'No columns declared — results show the raw column names.',
    cotField: 'Returned column',
    cotNhan: 'Display label',
    cotNhanPlaceholder: 'e.g. Revenue (VND)',
    kieuText: 'Text',
    kieuSo: 'Number',
    kieuTien: 'Currency',
    kieuPhanTram: 'Percent',
    kieuNgay: 'Date',
    chen: 'Insert:',
    chenO: 'Insert {{o}} into the summary template',
    nangCao: 'Advanced',
    chayThuCanLuu: 'Save the template before test-running it.',
    chuaChayThuPhien: 'Not test-run in this session yet. Results will appear here.',
    bangThamSo: 'Template parameters',
    bangCot: 'Template display columns',
    bangKetQua: 'First ten rows of the test run',
    caiNay: 'Explanation',
  },
  troLyAiKhuonView: {
    tieuDe: 'Query templates',
    phuDe:
      "Pre-computed business queries — sample SQL, database procedures or the customer's APIs. The assistant picks a template and fills in parameters instead of writing calculations itself.",
    theoKhuon: 'Answers from query templates',
    theoBanKhai: 'Answers from business declaration',
    moiCheDo: 'All answer modes',
    chuaCoKhuon: 'No query templates yet',
    khongDemDuoc: "Couldn't read the template list",
    demKhuon: '{{tong}} templates · {{sanSang}} in use',
    tatCaNguon: 'All sources',
    khongCoNguon: 'Source not found',
    khongCoNguonMoTa: "Source “{{ma}}” doesn't exist or this account can't see it.",
    nguonApiGhi:
      'API sources always answer from query templates. Each template is a business API; once it test-runs successfully and is granted in Data permissions, the assistant can use it.',
  },
};
